// Customizable Area Start
import React from "react";
import {
  BottomNavigation,
  BottomNavigationAction,
  Badge,
} from "@material-ui/core";
import { styled } from "@material-ui/core/styles";
import { ContactsIcon, NotificationIcon, ChatNavIcon, addIcon,  HomeIcon } from "./assets"
import MobileNavMenuController from "./MobileNavMenuController.web";
import { getToken } from "../../../components/src/AuthService";
const baseURLConfig = require("../../../framework/src/config.js");
const notificationsConfig = require("../../notifications/src/config.js");

const StyledBottomNavigation = styled(BottomNavigation)({
  backgroundColor: "#fff",
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  width: "100%",
  height: "calc(64px + env(safe-area-inset-bottom))",
  boxShadow: "0px -3px 5px -1px rgba(0,0,0,0.1)",
  zIndex: 1202,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  "&.MuiBottomNavigation-root": {
    alignItems: "center",
  },

  "& .MuiBottomNavigationAction-root": {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  "@media (min-width: 1150px)": {
    display: "none",
  },

  "&.bounce": {
    transform: "translateY(-5px)",
    transition: "transform 0.3s ease-in-out",
  },
});

const StyledBottomNavigationAction = styled(BottomNavigationAction)<{
  $active?: boolean;
}>((props: any) => ({
  padding: "0 !important",
  minWidth: "auto",
  height: "100%",
  "& .MuiSvgIcon-root": {
    fontSize: "22px",
    color: "#000",
  },
  "&.Mui-selected": {
    color: "#000",
  },
  "& .MuiBottomNavigationAction-label": {
    fontSize: "10px",
    fontWeight: 400,
    lineHeight: "20px",
    fontFamily: "Inter, 'sans-serif'",
  },
  "& .MuiBottomNavigationAction-wrapper": {
    height: "64px",
    width: "64px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    backgroundColor: props.$active ? "#FFC629" : "transparent",
  },
})) as React.ComponentType<React.ComponentProps<typeof BottomNavigationAction> & { $active?: boolean }>;

const StyledMenuIcon = styled("img")({
  width: "18px",
  height: "18px"
});

const StyledBadge = styled(Badge)({
  "& .MuiBadge-badge": {
    backgroundColor: "#FF0000",
    color: "#FFFFFF",
    fontSize: "10px",
    fontWeight: 700,
    minWidth: "18px",
    height: "18px",
    padding: "0 4px",
  },
});

const MENUS = [
  {
    value: 0,
    label: "Home",
    icon: <StyledMenuIcon src={HomeIcon} alt="" />,
  },
  {
    value: 1,
    label: "Messages",
    icon: <StyledMenuIcon src={ChatNavIcon} alt="" />,
  },
  {
    value: 2,
    icon: (
      <StyledMenuIcon
        src={addIcon}
        alt=""
        style={{ width: "30px", height: "30px" }}
      />
    ),
    isQuickAccess: true,
    onClick: (openModal: (e?: any) => void, event: any) => {
      openModal(event);
    },
  },
  {
    value: 3,
    label: "Contacts",
    icon: <StyledMenuIcon src={ContactsIcon} alt="" />,
  },
  {
    value: 4,
    label: "Notifications",
    icon: <StyledMenuIcon src={NotificationIcon} alt="" />,
  },
];

class MobileNavMenu extends MobileNavMenuController {
  notificationInterval: number | null = null;
  viewportRAF: number | null = null;
  lastViewportHeight: number = 0;
  isUserScrolling: boolean = false;
  scrollEndTimeout: number | null = null;
  navElement: HTMLElement | null = null;

  constructor(props: any) {
    super(props);
    this.state = {
      ...this.state,
      unreadCount: 0,
      bottomOffset: 0,
      isBouncing: false,
    };
  }

  async componentDidMount() {
    await super.componentDidMount?.();
    this.fetchUnreadNotificationCount();

    // Refresh count every 30 seconds
    this.notificationInterval = window.setInterval(() => {
      this.fetchUnreadNotificationCount();
    }, 30000);

    // Expose function to refresh notification count from other components
    if (typeof window !== 'undefined') {
      (window as any).refreshMobileNotificationCount = this.fetchUnreadNotificationCount;
    }

    // Initialize viewport tracking
    this.initializeViewportTracking();
  }

  async componentWillUnmount() {
    await super.componentWillUnmount?.();
    
    if (this.notificationInterval !== null) {
      clearInterval(this.notificationInterval);
    }

    if (this.viewportRAF !== null) {
      cancelAnimationFrame(this.viewportRAF);
    }

    if (this.scrollEndTimeout !== null) {
      clearTimeout(this.scrollEndTimeout);
    }
    
    // Remove all event listeners
    window.removeEventListener('scroll', this.handleScrollStart, true);
    window.removeEventListener('scroll', this.handleScrollBounce);

    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.handleViewportChange);
    }
  }

  initializeViewportTracking = () => {
    // Track user scrolling
    window.addEventListener('scroll', this.handleScrollStart, { passive: true, capture: true });
    window.addEventListener('scroll', this.handleScrollBounce, { passive: true });

    if (window.visualViewport) {
      this.lastViewportHeight = window.visualViewport.height;
      window.visualViewport.addEventListener('resize', this.handleViewportChange);

      // Initial update
      this.updateNavPosition();
    }
  };

  handleScrollStart = () => {
    // User is actively scrolling
    this.isUserScrolling = true;

    // Clear any existing timeout
    if (this.scrollEndTimeout !== null) {
      clearTimeout(this.scrollEndTimeout);
    }

    // Mark scrolling as ended after no scroll events for 100ms
    this.scrollEndTimeout = window.setTimeout(() => {
      this.isUserScrolling = false;
      // Update position once scrolling fully stops
      this.updateNavPosition();
    }, 100);
  };

  handleViewportChange = () => {
    // Only update if user is NOT actively scrolling
    // This prevents bounce during over-scroll
    if (!this.isUserScrolling) {
      // Use RAF to batch updates
      if (this.viewportRAF === null) {
        this.viewportRAF = requestAnimationFrame(() => {
          this.updateNavPosition();
          this.viewportRAF = null;
        });
      }
    }
  };

  handleScrollBounce = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const docHeight = document.documentElement.scrollHeight;

    if (scrollTop + windowHeight >= docHeight - 10) {
      if (!this.state.isBouncing) {
        this.setState({ isBouncing: true });
        setTimeout(() => this.setState({ isBouncing: false }), 500);
      }
    }
  };

  updateNavPosition = () => {
    if (!window.visualViewport) return;

    const viewportHeight = window.visualViewport.height;
    const windowHeight = window.innerHeight;
    
    // Calculate the UI height (URL bar, etc.)
    const uiHeight = windowHeight - viewportHeight;
    
    // Only update if there's a meaningful change (> 10px threshold)
    // This prevents micro-adjustments
    if (Math.abs(uiHeight - (this.state.bottomOffset || 0)) > 10) {
      // Check if viewport height changed significantly (URL bar hide/show)
      const heightDiff = Math.abs(viewportHeight - this.lastViewportHeight);
      
      if (heightDiff > 30) {
        // Significant height change = URL bar hide/show
        // Update immediately
        this.setState({ bottomOffset: uiHeight });
        this.lastViewportHeight = viewportHeight;
      } else if (!this.isUserScrolling) {
        // Small change and not scrolling = safe to update
        this.setState({ bottomOffset: uiHeight });
        this.lastViewportHeight = viewportHeight;
      }
    }
  };

  fetchUnreadNotificationCount = async () => {
    try {
      const baseURL = baseURLConfig.baseURL || window.location.origin;
      const endpoint = notificationsConfig.notificationEndPoint.startsWith('/') 
        ? notificationsConfig.notificationEndPoint 
        : `/${notificationsConfig.notificationEndPoint}`;
      const fullURL = `${baseURL}${endpoint}`;
      
      const response = await fetch(fullURL, {
        method: notificationsConfig.getDataMethod,
        headers: {
          "Content-Type": notificationsConfig.apiContentType,
          token: getToken() || "",
        },
      });
      
      if (response.ok) {
        const responseJson = await response.json();
        if (responseJson?.data) {
          const unreadNotifications = responseJson.data.filter(
            (notification: any) => !notification.attributes?.is_read
          );
          this.setState({ unreadCount: unreadNotifications.length });
        }
      }
    } catch (error) {
      console.error("Error fetching unread notification count:", error);
    }
  };

  markAllNotificationsAsRead = async () => {
    try {
      const baseURL = baseURLConfig.baseURL || window.location.origin;
      const endpoint = notificationsConfig.markasReadEndpoint.startsWith('/') 
        ? notificationsConfig.markasReadEndpoint 
        : `/${notificationsConfig.markasReadEndpoint}`;
      const fullURL = `${baseURL}${endpoint}`;
      
      const response = await fetch(fullURL, {
        method: notificationsConfig.markAsReadMethod,
        headers: {
          "Content-Type": notificationsConfig.apiContentType,
          token: getToken() || "",
        },
      });
      
      if (response.ok) {
        this.setState({ unreadCount: 0 });
        setTimeout(() => {
          this.fetchUnreadNotificationCount();
        }, 500);
      } else {
        console.error("Failed to mark all notifications as read:", response.status, response.statusText);
      }
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    // Don't change tab for add button (value 2)
    if (newValue === 2) {
      const { openModal } = this.props;
      if (openModal) {
        const syntheticEvent = { ...event, currentTarget: event.currentTarget } as any;
        openModal(syntheticEvent);
      }
      return;
    }
    
    this.setState({ currentTab: newValue });
    
    if (newValue === 4) {
      this.markAllNotificationsAsRead();
    }
    
    switch (newValue) {
      case 0:
        if (window.location.pathname.toLowerCase().includes('dashboard')) {
          const w: any = window as any;
          if (w && typeof w.handleHomeFromReels === 'function') {
            w.handleHomeFromReels();
          } else {
            if (w && typeof w.forceScrollToTop === 'function') {
              w.forceScrollToTop();
            }
            if (w && typeof w.resetToHomeView === 'function') {
              w.resetToHomeView();
            }
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
          }
        } else {
          this.handleRedirect("Dashboard");
        }
        break;
      case 1:
        this.handleRedirect("Messages");
        break;
      case 3:
        this.handleRedirect("ContactsList");
        break;
      case 4:
        this.handleRedirect("Notifications");
        break;
      default:
        break;
    }
  };

  render() {
    const { currentTab, unreadCount = 0, bottomOffset = 0 } = this.state;
    const { openModal } = this.props;

    return (
      <StyledBottomNavigation
        showLabels
        data-test-id="nav-menu-wrapper"
        value={currentTab}
        onChange={this.handleChange}
        className={this.state.isBouncing ? 'bounce' : ''}
        style={{
          bottom: `${-bottomOffset}px`,
          transition: this.isUserScrolling ? 'none' : 'bottom 0.2s ease-out'
        }}
      >
        {MENUS.map((menu) => {
          const { icon, label, value } = menu;
          const isNotification = label === "Notifications";
          
          return (
            <StyledBottomNavigationAction
              key={value}
              showLabel
              label={label}
              icon={
                isNotification && unreadCount > 0 ? (
                  <StyledBadge badgeContent={unreadCount} max={99}>
                    {icon}
                  </StyledBadge>
                ) : (
                  icon
                )
              }
              data-test-id={`nav-menu-${label || `icon-${value}`}`}
              $active={currentTab === value}
            />
          );
        })}
      </StyledBottomNavigation>
    );
  }
}

export default MobileNavMenu;
// Customizable Area End